'use client';
/** @jsxImportSource @emotion/react */

import { forwardRef, type Ref, type ElementType } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiTablePagination, {
  type TablePaginationTypeMap as MuiTablePaginationTypeMap,
} from '@mui/material/TablePagination';

import { CoreIcon } from '../core-icon/CoreIcon';

type ExcludedMuiProps = '';
type CustomProps = {
  component?: ElementType;
};

type TablePaginationTypeMap<
  P = CustomProps,
  D extends ElementType = MuiTablePaginationTypeMap<
    CustomProps,
    'div'
  >['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<
    MuiTablePaginationTypeMap<CustomProps, 'div'>['props'],
    ExcludedMuiProps
  > &
    P;
};

export type TablePaginationProps<
  D extends ElementType = MuiTablePaginationTypeMap<
    CustomProps,
    'div'
  >['defaultComponent'],
  P = CustomProps,
> = OverrideProps<TablePaginationTypeMap<P, D>, D>;

function FirstIcon() {
  return <CoreIcon name="chevronstart" size="md" />;
}

function LastIcon() {
  return <CoreIcon name="chevronend" size="md" />;
}

function NextIcon() {
  return <CoreIcon name="chevronright" size="md" />;
}

function PreviousIcon() {
  return <CoreIcon name="chevronleft" size="md" />;
}

const TablePaginationComponent = (
  { slots, ...props }: TablePaginationProps,
  ref: Ref<HTMLTableCellElement>
) => {
  return (
    <MuiTablePagination
      {...props}
      ref={ref}
      slots={{
        ...slots,
        actions: {
          firstButtonIcon: FirstIcon,
          lastButtonIcon: LastIcon,
          nextButtonIcon: NextIcon,
          previousButtonIcon: PreviousIcon,
          ...slots?.actions,
        },
      }}
    />
  );
};

TablePaginationComponent.displayName = 'TablePagination';

export const TablePagination = forwardRef(
  TablePaginationComponent
) as OverridableComponent<TablePaginationTypeMap>;
